import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Calculator, Shield, AlertCircle, Phone, CheckCircle, Heart, Loader2, Plane } from 'lucide-react';
import WinQuoteForm from './WinQuoteForm';
import { trackQuoteEvent } from '../lib/quoteAnalytics';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Life Insurance base rates per $100,000 coverage (monthly)
// Based on 2024-2025 Canadian market rates from Sun Life, Manulife, Canada Life
const LIFE_BASE_RATES = {
  term10: {
    male: { nonSmoker: 18.50, smoker: 45.00 },
    female: { nonSmoker: 14.50, smoker: 38.00 }
  },
  term20: {
    male: { nonSmoker: 24.00, smoker: 72.00 },
    female: { nonSmoker: 19.00, smoker: 58.00 }
  },
  term30: {
    male: { nonSmoker: 38.00, smoker: 115.00 },
    female: { nonSmoker: 30.00, smoker: 92.00 }
  }
};

// Critical Illness base rates per $100,000 coverage (monthly)
// Based on 2024-2025 Canadian market rates - CI is significantly more expensive than life
const CI_BASE_RATES = {
  term10: {
    male: { nonSmoker: 32.00, smoker: 52.00 },
    female: { nonSmoker: 38.00, smoker: 62.00 }
  },
  term20: {
    male: { nonSmoker: 48.00, smoker: 85.00 },
    female: { nonSmoker: 55.00, smoker: 98.00 }
  },
  toAge75: {
    male: { nonSmoker: 72.00, smoker: 145.00 },
    female: { nonSmoker: 82.00, smoker: 165.00 }
  }
};

// Visitors to Canada Insurance base rates (daily rates per $100,000 coverage)
// Based on 2024-2025 Canadian travel insurance market rates (Manulife, TuGo, Destination Canada)
const VISITOR_BASE_RATES = {
  // Daily rates by age group (per $100,000 coverage)
  '0-25': 1.80,
  '26-40': 2.20,
  '41-54': 3.50,
  '55-59': 5.00,
  '60-64': 7.50,
  '65-69': 10.50,
  '70-74': 15.00,
  '75-79': 22.00,
  '80-84': 35.00,
  '85+': 55.00
};

// Deductible multipliers for visitor insurance
const VISITOR_DEDUCTIBLE_MULTIPLIERS = {
  '0': 1.00,      // No deductible
  '100': 0.90,    // $100 deductible - 10% discount
  '250': 0.82,    // $250 deductible - 18% discount
  '500': 0.75,    // $500 deductible - 25% discount
  '1000': 0.65,   // $1000 deductible - 35% discount
  '2500': 0.55    // $2500 deductible - 45% discount
};

// Age multipliers - base rate is calibrated for age 35
// Life insurance rates increase moderately with age
// Critical illness rates increase more sharply after 40
const LIFE_AGE_MULTIPLIERS = {
  18: 0.75, 19: 0.77, 20: 0.80, 21: 0.82, 22: 0.84, 23: 0.86, 24: 0.88,
  25: 0.90, 26: 0.91, 27: 0.93, 28: 0.95, 29: 0.97, 30: 0.98,
  31: 0.99, 32: 0.99, 33: 1.00, 34: 1.00, 35: 1.00,
  36: 1.02, 37: 1.05, 38: 1.08, 39: 1.12, 40: 1.18,
  41: 1.25, 42: 1.32, 43: 1.40, 44: 1.48, 45: 1.58,
  46: 1.68, 47: 1.80, 48: 1.92, 49: 2.05, 50: 2.20,
  51: 2.36, 52: 2.54, 53: 2.73, 54: 2.94, 55: 3.18,
  56: 3.44, 57: 3.72, 58: 4.02, 59: 4.35, 60: 4.72,
  61: 5.12, 62: 5.55, 63: 6.02, 64: 6.52, 65: 7.08
};

const CI_AGE_MULTIPLIERS = {
  18: 0.65, 19: 0.68, 20: 0.70, 21: 0.73, 22: 0.75, 23: 0.78, 24: 0.80,
  25: 0.82, 26: 0.85, 27: 0.87, 28: 0.90, 29: 0.93, 30: 0.95,
  31: 0.97, 32: 0.98, 33: 0.99, 34: 1.00, 35: 1.00,
  36: 1.05, 37: 1.12, 38: 1.20, 39: 1.30, 40: 1.42,
  41: 1.55, 42: 1.70, 43: 1.87, 44: 2.05, 45: 2.28,
  46: 2.52, 47: 2.80, 48: 3.10, 49: 3.45, 50: 3.85,
  51: 4.30, 52: 4.80, 53: 5.35, 54: 5.98, 55: 6.70,
  56: 7.50, 57: 8.40, 58: 9.40, 59: 10.50, 60: 11.80,
  61: 13.20, 62: 14.80, 63: 16.60, 64: 18.60, 65: 20.90
};

const QuoteEstimator = () => {
  const [insuranceType, setInsuranceType] = useState('life'); // 'life', 'critical', or 'visitor'
  const [formData, setFormData] = useState({
    age: '',
    gender: '',
    smoker: '',
    coverageAmount: '',
    termLength: '',
    // Visitor insurance specific fields
    tripDuration: '',
    deductible: '',
    preExisting: ''
  });
  
  const [quote, setQuote] = useState(null);
  const [showContactForm, setShowContactForm] = useState(false);
  const [contactData, setContactData] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [submitted, setSubmitted] = useState(false);

  // Analytics: fire page-view + initial tab-load once, then every tab change
  useEffect(() => {
    trackQuoteEvent('quote_page_view');
    trackQuoteEvent('quote_tab_click', { tab: 'life' });
     
  }, []);

  const handleTypeChange = (type) => {
    trackQuoteEvent('quote_tab_click', { tab: type });
    setInsuranceType(type);
    setFormData({ 
      age: '', gender: '', smoker: '', coverageAmount: '', termLength: '',
      tripDuration: '', deductible: '', preExisting: ''
    });
    setQuote(null);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setQuote(null);
  };

  const handleContactChange = (e) => {
    setContactData({ ...contactData, [e.target.name]: e.target.value });
  };

  const calculateQuote = (e) => {
    e.preventDefault();
    
    const age = parseInt(formData.age);

    // Visitor to Canada Insurance calculation
    if (insuranceType === 'visitor') {
      const tripDuration = parseInt(formData.tripDuration);
      const coverageAmount = parseInt(formData.coverageAmount);
      const deductible = formData.deductible;
      const preExisting = formData.preExisting;

      // Get age group rate
      let ageGroup;
      if (age <= 25) ageGroup = '0-25';
      else if (age <= 40) ageGroup = '26-40';
      else if (age <= 54) ageGroup = '41-54';
      else if (age <= 59) ageGroup = '55-59';
      else if (age <= 64) ageGroup = '60-64';
      else if (age <= 69) ageGroup = '65-69';
      else if (age <= 74) ageGroup = '70-74';
      else if (age <= 79) ageGroup = '75-79';
      else if (age <= 84) ageGroup = '80-84';
      else ageGroup = '85+';

      const dailyRate = VISITOR_BASE_RATES[ageGroup];
      const coverageUnits = coverageAmount / 100000;
      const deductibleMultiplier = VISITOR_DEDUCTIBLE_MULTIPLIERS[deductible] || 1.0;
      
      // Pre-existing condition adds 25% if included
      const preExistingMultiplier = preExisting === 'yes' ? 1.25 : 1.0;

      // Calculate total premium
      const totalPremium = dailyRate * tripDuration * coverageUnits * deductibleMultiplier * preExistingMultiplier;

      // Add variance (±10%)
      const lowEstimate = totalPremium * 0.90;
      const highEstimate = totalPremium * 1.10;

      setQuote({
        type: 'visitor',
        low: lowEstimate.toFixed(2),
        high: highEstimate.toFixed(2),
        average: totalPremium.toFixed(2),
        coverageAmount: coverageAmount.toLocaleString(),
        tripDuration: tripDuration,
        deductible: deductible === '0' ? 'None' : `$${parseInt(deductible).toLocaleString()}`,
        preExisting: preExisting === 'yes' ? 'Included' : 'Not Included',
        age,
        dailyRate: (totalPremium / tripDuration).toFixed(2)
      });
      return;
    }

    // Life and Critical Illness calculation (existing logic)
    const gender = formData.gender;
    const smoker = formData.smoker;
    const coverageAmount = parseInt(formData.coverageAmount);
    const termLength = formData.termLength;

    // Get base rate based on insurance type
    const smokerKey = smoker === 'yes' ? 'smoker' : 'nonSmoker';
    let baseRate;
    let ageMultiplier;
    
    if (insuranceType === 'life') {
      const termKey = `term${termLength}`;
      baseRate = LIFE_BASE_RATES[termKey][gender][smokerKey];
      ageMultiplier = LIFE_AGE_MULTIPLIERS[age];
      if (!ageMultiplier) {
        if (age < 18) ageMultiplier = 0.70;
        else if (age > 65) ageMultiplier = 7.08 * Math.pow(1.08, age - 65);
      }
    } else {
      // Critical Illness
      const termKey = termLength === '75' ? 'toAge75' : `term${termLength}`;
      baseRate = CI_BASE_RATES[termKey][gender][smokerKey];
      ageMultiplier = CI_AGE_MULTIPLIERS[age];
      if (!ageMultiplier) {
        if (age < 18) ageMultiplier = 0.60;
        else if (age > 65) ageMultiplier = 20.90 * Math.pow(1.12, age - 65);
      }
    }

    // Calculate monthly premium
    const coverageUnits = coverageAmount / 100000;
    const monthlyPremium = baseRate * ageMultiplier * coverageUnits;

    // Add variance for realism (±8% to account for different insurers)
    const lowEstimate = monthlyPremium * 0.92;
    const highEstimate = monthlyPremium * 1.08;

    setQuote({
      type: insuranceType,
      low: lowEstimate.toFixed(2),
      high: highEstimate.toFixed(2),
      average: monthlyPremium.toFixed(2),
      coverageAmount: coverageAmount.toLocaleString(),
      termLength: termLength === '75' ? 'To Age 75' : `${termLength} Years`,
      age,
      gender: gender === 'male' ? 'Male' : 'Female',
      smoker: smoker === 'yes' ? 'Smoker' : 'Non-Smoker'
    });
  };

  const [isSubmittingQuote, setIsSubmittingQuote] = useState(false);
  
  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsSubmittingQuote(true);
    
    try {
      const requestBody = {
        name: contactData.name,
        email: contactData.email,
        phone: contactData.phone,
        insurance_type: quote.type,
        age: quote.age,
        gender: formData.gender || 'not_applicable',
        smoker: formData.smoker || 'not_applicable',
        coverage_amount: parseInt(formData.coverageAmount),
        term_length: insuranceType === 'visitor' ? formData.tripDuration : formData.termLength,
        estimated_low: quote.low,
        estimated_high: quote.high
      };
      
      const response = await fetch(`${API_URL}/api/quote-request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });
      
      if (response.ok) {
        setSubmitted(true);
        setTimeout(() => {
          setShowContactForm(false);
          setSubmitted(false);
          setContactData({ name: '', email: '', phone: '' });
        }, 3000);
      }
    } catch (error) {
      console.error('Error submitting quote request:', error);
    } finally {
      setIsSubmittingQuote(false);
    }
  };

  // Visitor to Canada coverage options
  const visitorCoverageOptions = [
    { value: '25000', label: '$25,000' },
    { value: '50000', label: '$50,000' },
    { value: '100000', label: '$100,000' },
    { value: '150000', label: '$150,000' },
    { value: '200000', label: '$200,000' },
    { value: '300000', label: '$300,000' }
  ];

  const visitorDeductibleOptions = [
    { value: '0', label: 'No Deductible' },
    { value: '100', label: '$100' },
    { value: '250', label: '$250' },
    { value: '500', label: '$500' },
    { value: '1000', label: '$1,000' },
    { value: '2500', label: '$2,500' }
  ];

  const tripDurationOptions = [
    { value: '7', label: '1 Week (7 days)' },
    { value: '14', label: '2 Weeks (14 days)' },
    { value: '30', label: '1 Month (30 days)' },
    { value: '60', label: '2 Months (60 days)' },
    { value: '90', label: '3 Months (90 days)' },
    { value: '120', label: '4 Months (120 days)' },
    { value: '180', label: '6 Months (180 days)' },
    { value: '365', label: '1 Year (365 days)' }
  ];

  const lifeCoverageOptions = [
    { value: '100000', label: '$100,000' },
    { value: '250000', label: '$250,000' },
    { value: '500000', label: '$500,000' },
    { value: '750000', label: '$750,000' },
    { value: '1000000', label: '$1,000,000' },
    { value: '1500000', label: '$1,500,000' },
    { value: '2000000', label: '$2,000,000' }
  ];

  const ciCoverageOptions = [
    { value: '25000', label: '$25,000' },
    { value: '50000', label: '$50,000' },
    { value: '75000', label: '$75,000' },
    { value: '100000', label: '$100,000' },
    { value: '150000', label: '$150,000' },
    { value: '200000', label: '$200,000' },
    { value: '250000', label: '$250,000' }
  ];

  const coverageOptions = insuranceType === 'life' ? lifeCoverageOptions : ciCoverageOptions;

  const lifeTermOptions = [
    { value: '10', label: '10 Years' },
    { value: '20', label: '20 Years' },
    { value: '30', label: '30 Years' }
  ];

  const ciTermOptions = [
    { value: '10', label: '10 Years' },
    { value: '20', label: '20 Years' },
    { value: '75', label: 'To Age 75' }
  ];

  const termOptions = insuranceType === 'life' ? lifeTermOptions : ciTermOptions;

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="bg-[#0077B6] p-6 text-white">
        <div className="flex items-center gap-3 mb-2">
          <Calculator className="h-8 w-8" />
          <h2 className="text-2xl font-bold">Insurance Quote Estimator</h2>
        </div>
        <p className="text-blue-100">Get an instant estimate for your insurance coverage</p>
      </div>

      <div className="p-6">
        {/* Insurance Type Tabs */}
        <div className="flex rounded-lg bg-gray-100 p-1 mb-6">
          <button
            type="button"
            onClick={() => handleTypeChange('life')}
            className={`flex-1 flex items-center justify-center gap-2 py-3 px-3 rounded-md font-semibold transition-all text-sm ${
              insuranceType === 'life'
                ? 'bg-[#0077B6] text-white shadow-md'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            <Shield className="h-4 w-4" />
            <span className="hidden sm:inline">Life Insurance</span>
            <span className="sm:hidden">Life</span>
          </button>
          <button
            type="button"
            onClick={() => handleTypeChange('critical')}
            className={`flex-1 flex items-center justify-center gap-2 py-3 px-3 rounded-md font-semibold transition-all text-sm ${
              insuranceType === 'critical'
                ? 'bg-[#0077B6] text-white shadow-md'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            <Heart className="h-4 w-4" />
            <span className="hidden sm:inline">Critical Illness</span>
            <span className="sm:hidden">Critical</span>
          </button>
          <button
            type="button"
            onClick={() => handleTypeChange('visitor')}
            className={`flex-1 flex items-center justify-center gap-2 py-3 px-3 rounded-md font-semibold transition-all text-sm ${
              insuranceType === 'visitor'
                ? 'bg-[#0077B6] text-white shadow-md'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            <Plane className="h-4 w-4" />
            <span className="hidden sm:inline">Visitors to Canada</span>
            <span className="sm:hidden">Visitors</span>
          </button>
        </div>

        {/* Disclaimer */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-amber-800">
            <strong>Please Note:</strong> {insuranceType === 'visitor'
              ? 'This calculator provides estimates only based on industry averages. Visitor insurance rates vary by provider and may require medical questionnaire. Pre-existing conditions coverage has waiting periods.'
              : 'Quotes are live from Canadian carriers via WinQuote®. Final premiums are confirmed by the insurer after underwriting.'
            }
            {' '}Contact our advisors for accurate, personalized quotes.
          </p>
        </div>

        {/* Life & Critical Illness — Live WinQuote */}
        {insuranceType !== 'visitor' && (
          <WinQuoteForm product={insuranceType} lang="en" apiUrl={API_URL} />
        )}

        {/* Visitor Insurance — In-house calculator */}
        {insuranceType === 'visitor' && (
        <form onSubmit={calculateQuote} className="space-y-5">
          {insuranceType === 'visitor' ? (
            /* Visitor Insurance Form */
            <div className="grid md:grid-cols-2 gap-5">
              {/* Age */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Visitor's Age
                </label>
                <input
                  type="number"
                  name="age"
                  value={formData.age}
                  onChange={handleChange}
                  min="0"
                  max="89"
                  required
                  placeholder="Enter visitor's age"
                  data-testid="visitor-age-input"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6] focus:border-transparent transition-all text-gray-800"
                />
              </div>

              {/* Trip Duration */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Trip Duration
                </label>
                <select
                  name="tripDuration"
                  value={formData.tripDuration}
                  onChange={handleChange}
                  required
                  data-testid="visitor-trip-duration"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6] focus:border-transparent transition-all text-gray-800"
                >
                  <option value="">Select duration</option>
                  {tripDurationOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              {/* Coverage Amount */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Coverage Amount
                </label>
                <select
                  name="coverageAmount"
                  value={formData.coverageAmount}
                  onChange={handleChange}
                  required
                  data-testid="visitor-coverage"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6] focus:border-transparent transition-all text-gray-800"
                >
                  <option value="">Select coverage amount</option>
                  {visitorCoverageOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              {/* Deductible */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Deductible (per claim)
                </label>
                <select
                  name="deductible"
                  value={formData.deductible}
                  onChange={handleChange}
                  required
                  data-testid="visitor-deductible"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6] focus:border-transparent transition-all text-gray-800"
                >
                  <option value="">Select deductible</option>
                  {visitorDeductibleOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              {/* Pre-existing Conditions */}
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Pre-existing Conditions Coverage
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <label
                    className={`flex items-center justify-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      formData.preExisting === 'no'
                        ? 'border-[#0077B6] bg-[#F0F7FF] text-[#0077B6]'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="preExisting"
                      value="no"
                      checked={formData.preExisting === 'no'}
                      onChange={handleChange}
                      data-testid="visitor-preexisting-no"
                      className="sr-only"
                    />
                    <span className="font-semibold">Not Required</span>
                  </label>
                  <label
                    className={`flex items-center justify-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      formData.preExisting === 'yes'
                        ? 'border-[#0077B6] bg-[#F0F7FF] text-[#0077B6]'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="preExisting"
                      value="yes"
                      checked={formData.preExisting === 'yes'}
                      onChange={handleChange}
                      data-testid="visitor-preexisting-yes"
                      className="sr-only"
                    />
                    <span className="font-semibold">Include Coverage (+25%)</span>
                  </label>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Pre-existing conditions typically have a stability period requirement (usually 90-180 days).
                </p>
              </div>
            </div>
          ) : (
            /* Life & Critical Illness Form */
            <div className="grid md:grid-cols-2 gap-5">
            {/* Age */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Your Age
              </label>
              <input
                type="number"
                name="age"
                value={formData.age}
                onChange={handleChange}
                min="18"
                max="75"
                required
                placeholder="Enter your age"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6] focus:border-transparent transition-all text-gray-800"
              />
            </div>

            {/* Gender */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Gender
              </label>
              <select
                name="gender"
                value={formData.gender}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6] focus:border-transparent transition-all text-gray-800"
              >
                <option value="">Select gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
            </div>

            {/* Smoker Status */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Do you smoke or use tobacco?
              </label>
              <select
                name="smoker"
                value={formData.smoker}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6] focus:border-transparent transition-all text-gray-800"
              >
                <option value="">Select status</option>
                <option value="no">No, I don't smoke</option>
                <option value="yes">Yes, I smoke/use tobacco</option>
              </select>
            </div>

            {/* Coverage Amount */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Coverage Amount
              </label>
              <select
                name="coverageAmount"
                value={formData.coverageAmount}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6] focus:border-transparent transition-all text-gray-800"
              >
                <option value="">Select coverage amount</option>
                {coverageOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            {/* Term Length */}
            <div className="md:col-span-2">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {insuranceType === 'life' ? 'Term Length' : 'Coverage Period'}
              </label>
              <div className="grid grid-cols-3 gap-3">
                {termOptions.map(term => (
                  <label
                    key={term.value}
                    className={`flex items-center justify-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      formData.termLength === term.value
                        ? 'border-[#0077B6] bg-[#F0F7FF] text-[#0077B6]'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="termLength"
                      value={term.value}
                      checked={formData.termLength === term.value}
                      onChange={handleChange}
                      className="sr-only"
                    />
                    <span className="font-semibold">{term.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          )}

          <Button
            type="submit"
            data-testid="visitor-calculate-btn"
            className="w-full bg-[#D4AF37] hover:bg-[#B8962E] text-[#003D5C] font-bold py-4 text-lg"
          >
            <Calculator className="h-5 w-5 mr-2" />
            Calculate My Estimate
          </Button>
        </form>
        )}

        {/* Quote Results */}
        {quote && (
          <div className="mt-8 animate-fadeIn" data-testid="quote-result-card">
            <Card className="bg-gradient-to-br from-[#0077B6] to-[#005A8C] text-white overflow-hidden">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-4">
                  {quote.type === 'life' ? (
                    <Shield className="h-6 w-6 text-[#D4AF37]" />
                  ) : quote.type === 'critical' ? (
                    <Heart className="h-6 w-6 text-[#D4AF37]" />
                  ) : (
                    <Plane className="h-6 w-6 text-[#D4AF37]" />
                  )}
                  <h3 className="text-xl font-bold">
                    {quote.type === 'life' ? 'Life Insurance' : 
                     quote.type === 'critical' ? 'Critical Illness' : 
                     'Visitors to Canada Insurance'} Estimate
                  </h3>
                </div>
                
                <div className="bg-white/10 rounded-xl p-6 mb-4">
                  <p className="text-blue-100 text-sm mb-1">
                    {quote.type === 'visitor' ? 'Estimated Total Premium' : 'Estimated Monthly Premium'}
                  </p>
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="text-4xl font-bold text-[#D4AF37]">${quote.low}</span>
                    <span className="text-2xl text-blue-200">-</span>
                    <span className="text-4xl font-bold text-[#D4AF37]">${quote.high}</span>
                    <span className="text-blue-200">
                      {quote.type === 'visitor' ? 'total' : '/month'}
                    </span>
                  </div>
                  {quote.type === 'visitor' && (
                    <p className="text-blue-200 text-sm mt-2">
                      (~${quote.dailyRate}/day)
                    </p>
                  )}
                </div>

                {quote.type === 'visitor' ? (
                  /* Visitor Insurance Details */
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-blue-200">Coverage Amount</p>
                      <p className="font-semibold">${quote.coverageAmount}</p>
                    </div>
                    <div>
                      <p className="text-blue-200">Trip Duration</p>
                      <p className="font-semibold">{quote.tripDuration} days</p>
                    </div>
                    <div>
                      <p className="text-blue-200">Age</p>
                      <p className="font-semibold">{quote.age} years old</p>
                    </div>
                    <div>
                      <p className="text-blue-200">Deductible</p>
                      <p className="font-semibold">{quote.deductible}</p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-blue-200">Pre-existing Conditions</p>
                      <p className="font-semibold">{quote.preExisting}</p>
                    </div>
                  </div>
                ) : (
                  /* Life/Critical Illness Details */
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-blue-200">Coverage Amount</p>
                      <p className="font-semibold">${quote.coverageAmount}</p>
                    </div>
                    <div>
                      <p className="text-blue-200">{quote.type === 'life' ? 'Term Length' : 'Coverage Period'}</p>
                      <p className="font-semibold">{quote.termLength}</p>
                    </div>
                    <div>
                      <p className="text-blue-200">Age</p>
                      <p className="font-semibold">{quote.age} years old</p>
                    </div>
                    <div>
                      <p className="text-blue-200">Profile</p>
                      <p className="font-semibold">{quote.gender}, {quote.smoker}</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Call to Action */}
            <div className="mt-6 p-6 bg-[#F0F7FF] rounded-xl border border-blue-100">
              <h4 className="font-bold text-gray-800 mb-2">Ready for an Accurate Quote?</h4>
              <p className="text-gray-600 text-sm mb-4">
                This estimate is based on industry averages. For a personalized quote with the best rates 
                from multiple insurers, speak with one of our licensed advisors.
              </p>
              
              {!showContactForm ? (
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    onClick={() => setShowContactForm(true)}
                    className="bg-[#0077B6] hover:bg-[#005A8C] text-white font-semibold flex-1"
                  >
                    Get Personalized Quote
                  </Button>
                  <Button
                    asChild
                    variant="outline"
                    className="border-2 border-[#0077B6] text-[#0077B6] hover:bg-[#0077B6] hover:text-white font-semibold flex-1"
                  >
                    <a href="tel:18772399060">
                      <Phone className="h-4 w-4 mr-2" />
                      Call 1-877-239-9060
                    </a>
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleContactSubmit} className="space-y-4">
                  {submitted ? (
                    <div className="flex items-center gap-3 text-green-600 bg-green-50 p-4 rounded-lg">
                      <CheckCircle className="h-6 w-6" />
                      <p className="font-semibold">Thank you! An advisor will contact you shortly.</p>
                    </div>
                  ) : (
                    <>
                      <div className="grid md:grid-cols-3 gap-3">
                        <input
                          type="text"
                          name="name"
                          value={contactData.name}
                          onChange={handleContactChange}
                          placeholder="Your Name"
                          required
                          className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6] focus:border-transparent"
                        />
                        <input
                          type="email"
                          name="email"
                          value={contactData.email}
                          onChange={handleContactChange}
                          placeholder="Email Address"
                          required
                          className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6] focus:border-transparent"
                        />
                        <input
                          type="tel"
                          name="phone"
                          value={contactData.phone}
                          onChange={handleContactChange}
                          placeholder="Phone Number"
                          required
                          className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6] focus:border-transparent"
                        />
                      </div>
                      <Button
                        type="submit"
                        disabled={isSubmittingQuote}
                        className="w-full bg-[#D4AF37] hover:bg-[#B8962E] text-[#003D5C] font-bold disabled:opacity-50"
                      >
                        {isSubmittingQuote ? (
                          <>
                            <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                            Submitting...
                          </>
                        ) : (
                          'Request Personalized Quote'
                        )}
                      </Button>
                    </>
                  )}
                </form>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuoteEstimator;
